import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaGoogle, FaEnvelope, FaLock, FaUser } from 'react-icons/fa';
import SEO from '../components/SEO';

const SignUp = () => {
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleGoogleSignUp = async () => {
    const { error } = await signIn({ provider: 'google' });
    if (error) {
      console.error('Error signing in with Google:', error);
    } else {
      navigate('/dashboard'); // Redirect to dashboard
    }
  };

  const handleCustomSignUp = async (e) => {
    e.preventDefault();
    const { error } = await signUp({
      email: formData.email,
      password: formData.password,
      fullName: formData.name,
    });

    if (error) {
      console.error('Error signing up:', error);
    } else {
      navigate('/dashboard'); // Redirect to dashboard
    }
  };

  return (
    <>
      <SEO 
        title="Sign Up - Viral Voice"
        description="Create your ViralVoice account and start automating your social media responses with AI that speaks in your voice."
      />
      <Link 
        to="https://www.viralvoice.co" 
        className="fixed top-6 left-6 flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors z-10"
      >
        <svg 
          className="w-5 h-5" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M10 19l-7-7m0 0l7-7m-7 7h18" 
          />
        </svg>
        Back to Home
      </Link>

      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-md w-full space-y-8 bg-gray-900/50 p-8 rounded-2xl backdrop-blur-xl border border-gray-800">
          <div>
            <h2 className="mt-2 text-center text-3xl font-extrabold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
              Create your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-400">
              Start managing your social media presence with AI
            </p>
          </div>

          {/* Google Sign Up Button */}
          <div>
            <button
              onClick={handleGoogleSignUp}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl text-white bg-gradient-to-r from-green-400 to-blue-500 hover:from-green-500 hover:to-blue-600 transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <FaGoogle className="h-5 w-5" />
              <span>Continue with Google</span>
            </button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-900 text-gray-400">Or continue with</span>
            </div>
          </div>

          {/* Custom Sign Up Form */}
          <form className="mt-8 space-y-6" onSubmit={handleCustomSignUp}>
            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <FaUser className="h-5 w-5" />
                </div>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="appearance-none relative block w-full pl-10 pr-3 py-3 bg-gray-800/50 border border-gray-700 placeholder-gray-400 text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                  placeholder="Full Name"
                />
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <FaEnvelope className="h-5 w-5" />
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="appearance-none relative block w-full pl-10 pr-3 py-3 bg-gray-800/50 border border-gray-700 placeholder-gray-400 text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                  placeholder="Email address"
                />
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <FaLock className="h-5 w-5" />
                </div>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="appearance-none relative block w-full pl-10 pr-3 py-3 bg-gray-800/50 border border-gray-700 placeholder-gray-400 text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                  placeholder="Password"
                />
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <FaLock className="h-5 w-5" />
                </div>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required
                  className="appearance-none relative block w-full pl-10 pr-3 py-3 bg-gray-800/50 border border-gray-700 placeholder-gray-400 text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                  placeholder="Confirm Password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-black bg-gradient-to-r from-green-400 to-blue-500 hover:from-green-500 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 transform hover:scale-[1.02]"
              >
                Create Account
              </button>
            </div>
          </form>

          <div className="text-center text-sm">
            <span className="text-gray-400">Already have an account? </span>
            <Link 
              to="/dashboard" 
              className="font-medium text-green-500 hover:text-green-400"
            >
              Sign in
            </Link>
          </div>

          {/* Background Decorations */}
          <div className="absolute top-0 left-0 -z-10 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
          <div className="absolute top-0 right-0 -z-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 -z-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        </div>
      </div>
    </>
  );
};

export default SignUp;